// The soak every game goes through, in the catalogue test and in the generator's validator:
// run a room with random-walk bots that mash both buttons, churn players in and out, keep the
// state inside its budget, audit static entities, and fingerprint the result for a
// determinism check. Throws with the tick number on the first thing wrong.

import type { GameModule } from "../contract.js";
import { Room } from "../server/room.js";
import { makeRng } from "../shared/rng.js";

export interface SoakOptions {
  ticks?: number;
  bots?: number;
  /** Bytes the serialised world may reach. */
  stateBudget?: number;
  /** Called after every step, for checks that need to watch play rather than the end state. */
  onTick?: (room: Room, tick: number) => void;
}

export interface SoakResult {
  hash: string;
  stepAvgMs: number;
  stepMaxMs: number;
  stateBytes: number;
  room: Room;
  ticks: number;
}

export const SOAK_DEFAULTS: Required<Omit<SoakOptions, "onTick">> = { ticks: 600, bots: 8, stateBudget: 256 * 1024 };

export function soak(mod: GameModule, seed: number, opts: SoakOptions = {}): SoakResult {
  const { ticks, bots: botCount, stateBudget } = { ...SOAK_DEFAULTS, ...opts };
  const onTick = opts.onTick;
  const room = new Room(mod, { id: `soak-${seed}`, seed });
  const rng = makeRng(seed * 7 + 1);
  const { w, h } = mod.meta.world;
  const bots = new Map<string, { tx: number; ty: number; at: number }>();
  let next = 0;
  const join = () => {
    const pid = `p${next++}`;
    room.join(pid, pid);
    bots.set(pid, { tx: w / 2, ty: h / 2, at: 0 });
  };
  for (let i = 0; i < Math.min(botCount, mod.meta.players.max); i++) join();

  // Static entities promise that their replicated fields never change after spawn. The runtime
  // takes that on trust (it is the point of the flag), so the soak checks it instead.
  const frozen = new Map<number, string>();
  const auditStatic = (tick: number) => {
    for (const e of room.liveEntities()) {
      const def = mod.entities.get(e.type)!;
      if (!def.static) continue;
      const now = JSON.stringify(def.replicated.map((f) => e[f]));
      const was = frozen.get(e.id);
      if (was === undefined) frozen.set(e.id, now);
      else if (was !== now) throw new Error(`static ${e.type} ${e.id} changed a replicated field at tick ${tick}: ${was} -> ${now}`);
    }
  };
  auditStatic(0);

  let stepTotal = 0;
  let stepMax = 0;
  for (let t = 0; t < ticks; t++) {
    // Churn: now and then someone leaves and someone else arrives.
    if (t % 97 === 50 && bots.size > 1) {
      const pid = [...bots.keys()][rng.int(bots.size)];
      room.leave(pid);
      bots.delete(pid);
    }
    if (t % 97 === 60 && bots.size < mod.meta.players.max) join();

    for (const [pid, b] of bots) {
      if (t >= b.at) {
        b.tx = rng.range(0, w);
        b.ty = rng.range(0, h);
        b.at = t + 5 + rng.int(40);
      }
      room.setInput(pid, { tx: b.tx, ty: b.ty, a: rng.next() < 0.25, b: rng.next() < 0.25 });
    }
    try {
      room.step();
    } catch (err) {
      throw new Error(`tick ${t}: ${(err as Error).message}`);
    }
    stepTotal += room.lastStepMs;
    if (room.lastStepMs > stepMax) stepMax = room.lastStepMs;
    onTick?.(room, t + 1);
    auditStatic(t + 1);
    const bytes = room.stateBytes();
    if (bytes > stateBudget) throw new Error(`state ${bytes} bytes over the ${stateBudget} byte budget at tick ${t}`);
    for (const pid of bots.keys()) {
      if (!room.playerEntity(pid)) throw new Error(`${pid} has no entity at tick ${t}: a player entity was despawned (reset it in place instead)`);
    }
  }
  for (const e of room.liveEntities()) {
    if (e.owner !== undefined && !bots.has(e.owner)) throw new Error(`orphan entity ${e.id} (${e.type}) owned by departed ${e.owner}`);
    for (const f of ["x", "y", "r"] as const) {
      if (!Number.isFinite(e[f])) throw new Error(`entity ${e.id} (${e.type}) has non-finite ${f}`);
    }
  }
  return { hash: room.hash(), stepAvgMs: stepTotal / ticks, stepMaxMs: stepMax, stateBytes: room.stateBytes(), room, ticks };
}
